/** Componenti riutilizzabili della console. */
import { localeCorrente, t } from '../i18n/lingua'
import { Link, useLocation } from 'react-router-dom'
import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'

/**
 * Un identificativo tecnico, mostrato per intero e copiabile.
 *
 * Serve per gli UUID: si leggono male, si ricopiano peggio, e sono esattamente
 * ciò che va incollato altrove — in un comando di manutenzione, in una
 * chiamata all'API, in un ticket. Finché non compaiono da nessuna parte nella
 * console, chi ne ha bisogno va a cercarli nel database.
 *
 * Il bottone «Copia» degrada senza rompersi: fuori da un contesto sicuro il
 * clipboard non esiste, e il valore resta comunque scritto e selezionabile.
 * Nasconderlo dietro al solo bottone lo renderebbe irraggiungibile proprio
 * dove serve — un'installazione raggiunta per indirizzo IP, in http.
 */
export function CopyableId({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false)

  async function copy() {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopied(false)
    }
  }

  return (
    <div className="copyable">
      <span className="copyable__label">{label}</span>
      <code className="copyable__value">{value}</code>
      <button className="linklike" onClick={copy} title={t('Copia {cosa}', { cosa: label })}>
        {copied ? t('copiato') : t('copia')}
      </button>
    </div>
  )
}

/**
 * Indicatore di stato: pallino colorato accanto al testo.
 *
 * Il colore da solo non basta — a chi non lo distingue direbbe nulla — quindi
 * l'etichetta resta sempre leggibile, in italiano: gli stati che arrivano
 * dall'API sono termini del protocollo (`succeeded`, `pending`), non parole
 * pensate per essere lette in una tabella.
 */
export function StatusBadge({ status }: { status: string }) {
  const tone = statusTone(status)
  return (
    <span className={`badge badge--${tone}`} title={status}>
      {/* Già tradotte alla dichiarazione: qui si mostra e basta. Il ripiego
          è lo stato grezzo, che non si traduce — è un codice, non una frase. */}
      {etichettaStato(status) ?? status}
    </span>
  )
}

// Le etichette si traducono **dove sono dichiarate**, non dove si usano.
//
// Passandole a `t` al momento di mostrarle, la frase italiana non compare mai
// dentro `t('…')`: chi legge il sorgente — una persona o la prova che confronta
// dizionario e codice — non ha modo di trovarla, e la traduzione risulterebbe
// scollegata pur essendo corretta.
//
// Dentro una funzione e non in una costante di modulo: `t` si chiama quando
// l'etichetta si mostra, così la lingua è sempre quella scelta.
export function etichettaStato(status: string): string | undefined {
  const etichette: Record<string, string> = {
    censo_appliance: t('Censo Appliance'),
    online: t('Online'),
    offline: t('Offline'),
    degraded: t('Degradata'),
    active: t('Attiva'),
    revoked: t('Revocata'),
    pending: t('In attesa'),
    assigned: t('Assegnato'),
    accepted: t('Preso in carico'),
    running: t('In esecuzione'),
    succeeded: t('Riuscito'),
    failed: t('Fallito'),
    partial: t('Parziale'),
    attiva: t('Attiva'),
    sospesa: t('Sospesa'),
    // Criticità di un servizio di business.
    critical: t('Critico'),
    high: t('Criticità alta'),
    medium: t('Criticità media'),
    low: t('Criticità bassa'),
  }
  return etichette[status]
}

/**
 * Lo stato nel tempo di un CI: attivo, silente, scomparso, dismesso.
 *
 * Ha una tabella sua e non usa quella generale per due motivi. Il primo è
 * grammaticale e non trascurabile: lì `active` è «Attiva» — si parla di sonde e
 * di credenziali — e un server non è attiva.
 *
 * Il secondo conta di più. «Dismesso» non è un guasto: è una decisione presa da
 * qualcuno, e va letta come una nota, non come un allarme. Colorarlo di rosso
 * come «scomparso» metterebbe sullo stesso piano «non lo troviamo più» — che
 * chiede di andare a vedere — e «l'abbiamo rottamato noi», che non chiede
 * niente.
 */
export function LifecycleBadge({ state }: { state: string | undefined }) {
  if (!state) return null
  return (
    <span className={`badge badge--${LIFECYCLE_TONE[state] ?? 'muted'}`} title={state}>
      {etichettaCicloVita(state) ?? state}
    </span>
  )
}

/**
 * Che cosa **fa** un CI, accanto a che cosa **è**.
 *
 * Un ruolo non sostituisce la classe: la affianca. La classe è una sola perché
 * è la dimensione su cui si filtra e si mappa verso un ITSM, ma un oggetto solo
 * fa più cose — un server che ospita venti macchine virtuali e che ha un iDRAC
 * che lo amministra è tre fatti, e finché l'unico posto per dirli era la classe
 * si contendevano lo stesso campo.
 *
 * Il `title` porta il **motivo**, con il numero dentro: «ospita 12 macchine
 * virtuali». Un ruolo senza la sua prova è un'etichetta che nessuno può
 * contestare, e quelle sono le etichette che restano sbagliate.
 */
export function RoleBadge({ role, reason }: { role: string; reason?: string }) {
  return (
    <span className="chip" title={reason || role}>
      {etichettaRuolo(role) ?? role}
    </span>
  )
}

//: Le etichette dei ruoli. Una guardia nel Control Plane
//: (`test_role_labels_match.py`) controlla che questa tabella e il catalogo in
//: `app/services/model/roles.py` dicano la stessa cosa: un ruolo aggiunto là e
//: dimenticato qui comparirebbe in console con il proprio nome tecnico.
export const ROLE_LABEL: Record<string, string> = {
  hypervisor: 'Ospita macchine virtuali',
  out_of_band_managed: 'Gestito fuori banda',
  cluster_member: 'Membro di un cluster',
}

/** L'etichetta **tradotta** di un ruolo. `ROLE_LABEL` resta la tabella in
 *  italiano che la guardia confronta con il modello; questa è quella da
 *  mostrare. Le frasi sono letterali perché la prova delle traduzioni le trovi. */
export function etichettaRuolo(role: string): string | undefined {
  const etichette: Record<string, string> = {
    hypervisor: t('Ospita macchine virtuali'),
    out_of_band_managed: t('Gestito fuori banda'),
    cluster_member: t('Membro di un cluster'),
  }
  return etichette[role]
}

/** L'etichetta tradotta di uno stato del ciclo di vita (vedi `LIFECYCLE_LABEL`). */
export function etichettaCicloVita(state: string): string | undefined {
  const etichette: Record<string, string> = {
    active: t('Attivo'),
    stale: t('Silente'),
    missing: t('Scomparso'),
    retired: t('Dismesso'),
  }
  return etichette[state]
}

export const LIFECYCLE_LABEL: Record<string, string> = {
  active: 'Attivo',
  stale: 'Silente',
  missing: 'Scomparso',
  retired: 'Dismesso',
}

const LIFECYCLE_TONE: Record<string, 'ok' | 'warn' | 'error' | 'muted' | 'info'> = {
  active: 'ok',
  stale: 'warn',
  missing: 'error',
  retired: 'muted',
}

function statusTone(status: string): 'ok' | 'warn' | 'error' | 'muted' | 'info' {
  switch (status) {
    case 'online':
    case 'succeeded':
    case 'active':
    case 'attiva':
      return 'ok'
    case 'degraded':
    case 'partial':
    case 'pending':
      return 'warn'
    case 'offline':
    case 'failed':
    case 'revoked':
    case 'critical':
      return 'error'
    case 'high':
      return 'warn'
    case 'running':
    case 'assigned':
    case 'accepted':
    case 'medium':
      return 'info'
    default:
      return 'muted'
  }
}

/** Riquadro con un numero in evidenza. */
export function StatCard({
  label,
  value,
  hint,
  tone,
  to,
  onClick,
}: {
  label: string
  value: number | string
  hint?: string
  tone?: 'ok' | 'warn' | 'error'
  /** Dove porta il clic: l'elenco degli oggetti contati. Un numero che non si
   * può aprire lascia la domanda «quali sono?» senza risposta. */
  to?: string
  /** In alternativa a `to`: apre qualcosa sul posto (un popup con la lista). */
  onClick?: () => void
}) {
  const contenuto = (
    <>
      <div className="stat__value">{value}</div>
      <div className="stat__label">{label}</div>
      {hint && <div className="stat__hint">{hint}</div>}
    </>
  )
  const classe = `stat${tone ? ` stat--${tone}` : ''}`
  if (to) return <Link to={to} className={`${classe} stat--link`}>{contenuto}</Link>
  if (onClick) {
    return (
      <button type="button" className={`${classe} stat--link`} onClick={onClick} style={{ textAlign: 'left' }}>
        {contenuto}
      </button>
    )
  }
  return <div className={classe}>{contenuto}</div>
}

/** Una voce della barra delle sezioni: identificativo e etichetta. */
export interface SezionePagina {
  id: string
  label: string
}

interface StatoSezioni {
  /** La sezione mostrata; ``null`` = nessun contenitore a sezioni intorno (si
   *  mostra tutto, come una pagina unica di prima). */
  active: string | null
  setActive: (id: string) => void
}

const SectionCtx = createContext<StatoSezioni>({ active: null, setActive: () => {} })

function _sezioneIniziale(sections: SezionePagina[]): string {
  // Un deep-link `#storico` apre dritto su quella sezione; altrimenti la prima.
  const h = decodeURIComponent((globalThis.location?.hash || '').replace(/^#/, ''))
  if (sections.some((s) => s.id === h)) return h
  return sections[0]?.id ?? ''
}

/**
 * Trasforma una pagina lunga in **sezioni**: si vede solo quella scelta, non
 * tutta la colonna.
 *
 * ## Perché
 *
 * La scheda di un CI è quindici riquadri; scorrerli tutti per arrivare allo
 * storico è il difetto che la barra delle sezioni già segnalava, ma non
 * risolveva — la pagina restava lunga. A sezioni, la barra **sostituisce** il
 * contenuto invece di scorrervi dentro: la pagina è corta e chi cerca una cosa
 * la apre con un clic.
 *
 * ## Come si condivide una sezione
 *
 * L'`id` della sezione attiva finisce nell'hash dell'URL (`#backup`), così un
 * collegamento a una sezione precisa si incolla e riapre lì; il tasto Indietro
 * la ripercorre. Si usa `replaceState`, non una voce di cronologia per clic:
 * cambiare scheda non è «navigare», e riempire la cronologia di sezioni
 * renderebbe il tasto Indietro inutile.
 *
 * ## La compatibilità
 *
 * Una `PageSection` **fuori** da una `SectionedPage` mostra il suo contenuto
 * come prima (colonna unica): le pagine non ancora convertite continuano a
 * funzionare senza saperne niente.
 */
export function SectionedPage({
  sections,
  children,
}: {
  sections: SezionePagina[]
  children: ReactNode
}) {
  const [active, setActive] = useState<string>(() => _sezioneIniziale(sections))

  const chiave = sections.map((s) => s.id).join('|')
  // A <Link to="/page#section"> on the same page changes the location without
  // a «hashchange» event: the router's hash is read too.
  const { hash } = useLocation()
  useEffect(() => {
    // Il deep-link e il tasto Indietro: l'hash è la fonte, lo stato la segue.
    const daHash = () => {
      const h = decodeURIComponent((window.location.hash || '').replace(/^#/, ''))
      if (sections.some((s) => s.id === h)) setActive(h)
    }
    daHash()
    window.addEventListener('hashchange', daHash)
    return () => window.removeEventListener('hashchange', daHash)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chiave, hash])

  const scegli = (id: string) => {
    setActive(id)
    // L'hash per il deep-link, senza aggiungere una voce di cronologia per clic.
    if (globalThis.history) globalThis.history.replaceState(null, '', `#${id}`)
  }

  // The bar shows the sections that fit; the others go into «Altro ▾». It
  // used to scroll sideways with the scrollbar hidden, so with twenty
  // sections the last ones were simply not there (the CI page).
  const navRef = useRef<HTMLElement>(null)
  const [larghezze, setLarghezze] = useState<number[] | null>(null)
  const [quante, setQuante] = useState(sections.length)
  const [menu, setMenu] = useState(false)
  useLayoutEffect(() => { setLarghezze(null); setQuante(sections.length) }, [chiave, sections.length])
  useLayoutEffect(() => {
    if (larghezze !== null || !navRef.current) return
    const voci = Array.from(navRef.current.querySelectorAll<HTMLElement>('[data-misura]'))
    setLarghezze(voci.map((v) => v.offsetWidth + 4))
  })
  useEffect(() => {
    const el = navRef.current
    if (!el || !larghezze) return
    const calcola = () => {
      const spazio = el.clientWidth
      if (larghezze.reduce((a, b) => a + b, 0) <= spazio) { setQuante(larghezze.length); return }
      let usato = 140 // room for «Altro (n) ▾»
      let n = 0
      for (const w of larghezze) {
        if (usato + w > spazio) break
        usato += w
        n++
      }
      setQuante(Math.max(1, n))
    }
    calcola()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(calcola)
    ro.observe(el)
    return () => ro.disconnect()
  }, [larghezze])
  useEffect(() => {
    if (!menu) return
    const chiudi = (e: Event) => {
      if (e instanceof KeyboardEvent && e.key !== 'Escape') return
      if (e instanceof MouseEvent && navRef.current?.querySelector('.pagenav__altro')?.contains(e.target as Node)) return
      setMenu(false)
    }
    document.addEventListener('mousedown', chiudi)
    document.addEventListener('keydown', chiudi)
    return () => { document.removeEventListener('mousedown', chiudi); document.removeEventListener('keydown', chiudi) }
  }, [menu])

  const misura = larghezze === null
  const visibili = misura ? sections : sections.slice(0, quante)
  const altre = misura ? [] : sections.slice(quante)
  const attivaFuori = altre.find((s) => s.id === active)

  return (
    <SectionCtx.Provider value={{ active, setActive }}>
      {sections.length >= 2 && (
        <nav ref={navRef} className="pagenav" aria-label={t('Sezioni della pagina')}>
          {visibili.map((s) => (
            <button
              key={s.id}
              type="button"
              data-misura={misura ? '' : undefined}
              className={`pagenav__link${active === s.id ? ' pagenav__link--active' : ''}`}
              aria-current={active === s.id ? 'true' : undefined}
              onClick={() => scegli(s.id)}
            >
              {s.label}
            </button>
          ))}
          {altre.length > 0 && (
            <div className="pagenav__altro">
              <button
                type="button"
                className={`pagenav__link${attivaFuori ? ' pagenav__link--active' : ''}`}
                aria-haspopup="menu"
                aria-expanded={menu}
                title={t('Altre sezioni ({n})', { n: altre.length })}
                onClick={() => setMenu((m) => !m)}
              >
                {attivaFuori ? attivaFuori.label : t('Altro ({n})', { n: altre.length })} ▾
              </button>
              {menu && (
                <div className="pagenav__menu" role="menu">
                  {altre.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      role="menuitem"
                      className={`pagenav__voce${active === s.id ? ' pagenav__voce--active' : ''}`}
                      onClick={() => { scegli(s.id); setMenu(false) }}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </nav>
      )}
      {children}
    </SectionCtx.Provider>
  )
}

/**
 * Una sezione della pagina.
 *
 * Dentro una `SectionedPage` si mostra **solo** quando è quella attiva; fuori
 * (nessun contenitore a sezioni) si mostra sempre — così le pagine non ancora
 * convertite restano una colonna unica, come prima. L'`id` resta sull'elemento
 * per il deep-link e per il margine d'ancora.
 */
export function PageSection({ id, children }: { id: string; children: ReactNode }) {
  const { active } = useContext(SectionCtx)
  if (active !== null && active !== id) return null
  return (
    <section id={id} className="pagesection">
      {children}
    </section>
  )
}

export function Card({
  title,
  actions,
  children,
}: {
  title?: string
  actions?: ReactNode
  children: ReactNode
}) {
  return (
    <section className="card">
      {(title || actions) && (
        <header className="card__header">
          {title && <h2 className="card__title">{title}</h2>}
          {actions && <div className="card__actions">{actions}</div>}
        </header>
      )}
      <div className="card__body">{children}</div>
    </section>
  )
}

/** Messaggio di errore non bloccante. */
export function ErrorBanner({ error }: { error: unknown }) {
  if (!error) return null
  const message = error instanceof Error ? error.message : String(error)
  return <div className="banner banner--error">{message}</div>
}

/** Stato vuoto con eventuale suggerimento sull'azione da compiere. */
export function EmptyState({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="empty">
      <p className="empty__title">{title}</p>
      {hint && <p className="empty__hint">{hint}</p>}
    </div>
  )
}

export function Spinner({ label = t('Caricamento…') }: { label?: string }) {
  // I tre pallini pulsanti dell'attesa (gli stessi del reindirizzamento SSO in
  // login): dice «sto lavorando» senza promettere una barra che non sappiamo
  // riempire. Usato ovunque si aspetti una risposta lenta (query firewall,
  // analisi routing, export, inventario…).
  return (
    <div className="spinner" role="status">
      <div className="sso-attesa" aria-hidden="true">
        <span />
        <span />
        <span />
      </div>
      {label && <div>{label}</div>}
    </div>
  )
}

/** Formatta un istante ISO in data/ora locale leggibile. */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString(localeCorrente(), { dateStyle: 'short', timeStyle: 'medium' })
}

/**
 * Formatta una data **senza ora**.
 *
 * Serve per i fatti che sono giorni e non istanti: un acquisto, una garanzia, una
 * scadenza contrattuale. Ricavarla tagliando `formatDateTime` lasciava la virgola
 * del separatore («15/03/24,»), che si nota subito e sembra un dato troncato.
 */
export function formatDay(value: string | null | undefined): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString(localeCorrente(), { day: '2-digit', month: '2-digit', year: 'numeric' })
}

/**
 * Quanto è durata una cosa, da un inizio a una fine (es. "2 min 5 s").
 *
 * Misura da `started_at`, non da `created_at`: la durata di un job è il tempo in
 * cui ha **girato**, non quello passato in coda in attesa di una sonda — sommare
 * l'attesa direbbe che una scansione da trenta secondi ne è durata dieci minuti.
 * Se non è ancora partito non c'è durata da mostrare (`—`). Se è partito ma non
 * finito si misura fino ad **adesso**: è la risposta a «da quanto sta girando»,
 * la domanda che ci si fa proprio sui job che non finiscono.
 */
export function formatDurata(
  start: string | null | undefined,
  end: string | null | undefined,
): string {
  if (!start) return '—'
  const inizio = new Date(start).getTime()
  const fine = end ? new Date(end).getTime() : Date.now()
  const sec = Math.floor((fine - inizio) / 1000)
  if (Number.isNaN(sec) || sec < 0) return '—'
  if (sec < 60) return `${sec} s`
  const h = Math.floor(sec / 3600)
  const min = Math.floor((sec % 3600) / 60)
  const s = sec % 60
  if (h > 0) return `${h} h ${min} min`
  return `${min} min ${s} s`
}

/** Descrive quanto tempo è passato da un istante (es. "2 min fa"). */
export function timeAgo(value: string | null | undefined): string {
  if (!value) return '—'
  const seconds = Math.floor((Date.now() - new Date(value).getTime()) / 1000)
  if (Number.isNaN(seconds)) return '—'
  // Una data futura (una scadenza) si dice «tra…»: prima usciva «-29815073s fa».
  if (seconds < 0) {
    const tra = -seconds
    if (tra < 60) return t('tra {n}s', { n: tra })
    if (tra < 3600) return t('tra {n} min', { n: Math.floor(tra / 60) })
    if (tra < 86400) return t('tra {n} h', { n: Math.floor(tra / 3600) })
    return t('tra {n} g', { n: Math.floor(tra / 86400) })
  }
  if (seconds < 60) return t('{n}s fa', { n: seconds })
  if (seconds < 3600) return t('{n} min fa', { n: Math.floor(seconds / 60) })
  if (seconds < 86400) return t('{n} h fa', { n: Math.floor(seconds / 3600) })
  return t('{n} g fa', { n: Math.floor(seconds / 86400) })
}

/**
 * A console path that came as data (a notification, a deadline): kept only
 * when it is a path inside the console. «//host», «/\host» and anything that
 * is not a path become the home page, so data can never turn a link into a
 * navigation to another site (react-router GHSA-wrjc-x8rr-h8h6).
 */
export function internalPath(link: string | null | undefined): string {
  const p = String(link ?? '')
  if (!p.startsWith('/') || p.startsWith('//') || p.includes('\\')) return '/'
  return p
}
